import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { type ReactNode, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addNotification, NotificationType } from 'modules/app/notifications';
import type { AppDispatch } from 'modules/app/redux/store';
import {
  createSupplier,
  updateSupplier,
} from 'modules/invoices/components/Invoices/Suppliers/thunks';
import { type ApiSupplier } from 'modules/invoices/components/Invoices/_shared/SupplierEditor/supplier';
import i18n from 'src/core/config/i18n';
import type { FormValues } from 'src/core/modules/invoices/components/Invoices/_shared/SupplierEditor/SupplierForm/SupplierForm';
import { useInvalidateSupplierQuery } from 'src/core/modules/suppliers/hooks';
import type { VerificationOfPayeeResult } from 'src/core/modules/verification-of-payee/apis';
import { useVerificationOfPayeeQuery } from 'src/core/modules/verification-of-payee/apis/useVerificationOfPayeeQuery';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

export type SupplierPersistenceManagerRenderProps = {
  onSave: (
    supplierId: string | undefined,
    payload: Partial<ApiSupplier>,
  ) => Promise<
    | { outcome: 'success' }
    | { outcome: 'failure'; error: unknown }
    | { outcome: 'no-change' }
  >;
  onTrackSupplierSave?: (suggestionContext: {
    isIbanDisplayed: boolean;
    suggestedIban: string | null;
    isBicDisplayed: boolean;
    suggestedBic: string | null;
  }) => void;
  isCheckingVerificationOfPayee?: boolean;
  onFormFieldUpdate: (field: keyof FormValues, formFields: FormValues) => void;
  currentVerificationOfPayee?: VerificationOfPayeeResult;
  onIgnoreVerificationOfPayee: () => void;
};

type Props = {
  supplier: ApiSupplier;
  onUpdateSupplierSuccess?: (supplier: ApiSupplier) => void;
  onCreateSupplierSuccess?: (supplier: ApiSupplier) => void;
  supportedMode?: Array<'creation' | 'edition'>;
  shouldTrackSave?: boolean;
  initialVerificationOfPayee?: VerificationOfPayeeResult;
  onVoPUpdate?: (vopResult: VerificationOfPayeeResult) => void;
  renderSupplierEditor: (
    props: SupplierPersistenceManagerRenderProps,
  ) => ReactNode;
  onVerificationOfPayeeUpdate?: (vopResult: VerificationOfPayeeResult) => void;
};

export const SupplierPersistenceManager = ({
  supplier,
  onUpdateSupplierSuccess,
  onCreateSupplierSuccess,
  supportedMode,
  shouldTrackSave,
  initialVerificationOfPayee,
  renderSupplierEditor,
  onVerificationOfPayeeUpdate,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { mutateAsync: checkVerificationOfPayee } =
    useVerificationOfPayeeQuery();
  const [isVerificationOfPayeeLoading, setIsVerificationOfPayeeLoading] =
    useState(false);
  const invalidateSupplierQuery = useInvalidateSupplierQuery();
  const [currentVerificationOfPayee, setCurrentVerificationOfPayee] = useState<
    VerificationOfPayeeResult | undefined
  >(initialVerificationOfPayee);

  const onUpdate = async (
    supplierId: string,
    payload: Partial<ApiSupplier>,
  ) => {
    const onUpdateResult = await dispatch(updateSupplier(supplierId, payload));

    if (onUpdateResult.outcome === 'success') {
      if (onUpdateResult.supplier.id) {
        invalidateSupplierQuery(onUpdateResult.supplier.id);
      }
      onUpdateSupplierSuccess?.(onUpdateResult.supplier);
    }
    return onUpdateResult;
  };

  const onCreate = async (payload: Partial<ApiSupplier>) => {
    const onCreateResult = await dispatch(createSupplier(payload));

    if (onCreateSupplierSuccess && onCreateResult.outcome === 'success') {
      onCreateSupplierSuccess(onCreateResult.supplier);
    }
    return onCreateResult;
  };

  const onSave = async (
    supplierId: string | undefined,
    payload: Partial<ApiSupplier>,
  ): Promise<
    | { outcome: 'success' }
    | { outcome: 'failure'; error: unknown }
    | { outcome: 'no-change' }
  > => {
    if (supplierId && hasNoChanges(supplier, payload)) {
      onUpdateSupplierSuccess?.(supplier);
      return { outcome: 'no-change' as const };
    }

    if (supplierId) {
      return onUpdate(supplierId, payload);
    }
    if (supportedMode && !supportedMode.includes('creation')) {
      // Edition mode only - supplier must have an ID
      return { outcome: 'failure' as const, error: 'Supplier ID is required' };
    }
    return onCreate(payload);
  };

  const runVerificationOfPayee = useMemo(
    () =>
      debounce(async (iban: string, accountHolderName: string) => {
        const verificationResult = await checkVerificationOfPayee({
          iban,
          name: accountHolderName,
          refresh: false,
        });

        setCurrentVerificationOfPayee(verificationResult);
        onVerificationOfPayeeUpdate?.(verificationResult);
        setIsVerificationOfPayeeLoading(false);

        if (verificationResult.matchingResult === 'match') {
          dispatch(
            addNotification({
              type: NotificationType.Success,
              message: i18n.t('verificationOfPayee.matchVerified', {
                name: accountHolderName,
              }),
            }),
          );
        }
      }, 500),
    [checkVerificationOfPayee, dispatch, onVerificationOfPayeeUpdate],
  );

  const onTrackSupplierSave = shouldTrackSave
    ? (suggestionContext: {
        isIbanDisplayed: boolean;
        suggestedIban: string | null;
        isBicDisplayed: boolean;
        suggestedBic: string | null;
      }) => {
        track(AnalyticEventName.SUPPLIER_SUGGESTION_DISPLAYED, {
          supplierId: supplier.id,
          ...suggestionContext,
        });
      }
    : undefined;

  const onIgnoreVerificationOfPayee = () => {
    setCurrentVerificationOfPayee(undefined);
    setIsVerificationOfPayeeLoading(false);
    runVerificationOfPayee.cancel();
  };

  return renderSupplierEditor({
    onSave,
    onTrackSupplierSave,
    currentVerificationOfPayee,
    onIgnoreVerificationOfPayee,
    isCheckingVerificationOfPayee: isVerificationOfPayeeLoading,
    onFormFieldUpdate: (field: keyof FormValues, formFields: FormValues) => {
      if (field !== 'iban' && field !== 'accountHolderName') {
        return;
      }

      const { iban, accountHolderName } = formFields;

      if (!iban || !accountHolderName) {
        runVerificationOfPayee.cancel();
        setIsVerificationOfPayeeLoading(false);
        return;
      }

      setIsVerificationOfPayeeLoading(true);
      runVerificationOfPayee(iban, accountHolderName);
    },
  });
};

const hasNoChanges = (
  originalSupplier: ApiSupplier,
  payload: Partial<ApiSupplier>,
): boolean => {
  const mergedSupplier = merge({}, originalSupplier, payload);
  return isEqual(originalSupplier, mergedSupplier);
};
